import { getEffectiveness } from './typeChart';
import MOVE_TYPES from './moves';
import MOVE_DATA from './moveData';

/**
 * Calculate a stat at level 50 with given base, EVs, and nature multiplier.
 */
export function calcStat(base, ev = 0, nature = 1.0, isHp = false) {
  const iv = 31;
  if (isHp) return Math.floor((2 * base + iv + Math.floor(ev / 4)) * 50 / 100 + 50 + 10);
  return Math.floor((Math.floor((2 * base + iv + Math.floor(ev / 4)) * 50 / 100) + 5) * nature);
}

// Nature stat multipliers
const NATURE_MODS = {
  Adamant:  { atk: 1.1, spa: 0.9 }, Bold:    { def: 1.1, atk: 0.9 },
  Brave:    { atk: 1.1, spe: 0.9 }, Calm:    { spd: 1.1, atk: 0.9 },
  Careful:  { spd: 1.1, spa: 0.9 }, Gentle:  { spd: 1.1, def: 0.9 },
  Hasty:    { spe: 1.1, def: 0.9 }, Impish:  { def: 1.1, spa: 0.9 },
  Jolly:    { spe: 1.1, spa: 0.9 }, Lax:     { def: 1.1, spd: 0.9 },
  Lonely:   { atk: 1.1, def: 0.9 }, Mild:    { spa: 1.1, def: 0.9 },
  Modest:   { spa: 1.1, atk: 0.9 }, Naive:   { spe: 1.1, spd: 0.9 },
  Naughty:  { atk: 1.1, spd: 0.9 }, Quiet:   { spa: 1.1, spe: 0.9 },
  Rash:     { spa: 1.1, spd: 0.9 }, Relaxed: { def: 1.1, spe: 0.9 },
  Sassy:    { spd: 1.1, spe: 0.9 }, Timid:   { spe: 1.1, atk: 0.9 },
  // Neutral natures
  Hardy: {}, Docile: {}, Serious: {}, Bashful: {}, Quirky: {},
};

export function getNatureMod(nature, stat) {
  const mods = NATURE_MODS[nature] || {};
  return mods[stat] || 1.0;
}

/**
 * Resolve EVs from either old-style `evs` or Pokemon Champions `statPoints` (1 SP = 8 EVs).
 */
function resolveEVs(pokemon) {
  if (pokemon.statPoints) {
    const sp = pokemon.statPoints;
    return {
      hp: (sp.hp || 0) * 8, atk: (sp.atk || 0) * 8, def: (sp.def || 0) * 8,
      spa: (sp.spa || 0) * 8, spd: (sp.spd || 0) * 8, spe: (sp.spe || 0) * 8,
    };
  }
  return pokemon.evs || {};
}

/**
 * Get effective stats for a Pokemon at level 50.
 * Supports both old EVs (preset teams) and Stat Points (custom teams, 1 SP = 8 EVs).
 */
export function getEffectiveStats(pokemon) {
  const base = pokemon.megaStats || pokemon.baseStats;
  if (!base) return null;
  const evs = resolveEVs(pokemon);
  const nature = pokemon.nature || '';

  return {
    hp: calcStat(base.hp, evs.hp || 0, 1.0, true),
    atk: calcStat(base.atk, evs.atk || 0, getNatureMod(nature, 'atk')),
    def: calcStat(base.def, evs.def || 0, getNatureMod(nature, 'def')),
    spa: calcStat(base.spa, evs.spa || 0, getNatureMod(nature, 'spa')),
    spd: calcStat(base.spd, evs.spd || 0, getNatureMod(nature, 'spd')),
    spe: calcStat(base.spe, evs.spe || 0, getNatureMod(nature, 'spe')),
  };
}

/**
 * Pokémon stat-stage multiplier for stages -6..+6.
 * +1 = 1.5x, +2 = 2x, +3 = 2.5x, ..., -1 = 0.67x, etc.
 */
export function stageMultiplier(stage) {
  if (!stage) return 1;
  if (stage > 0) return (2 + stage) / 2;
  return 2 / (2 - stage);
}

/**
 * Get effective speed considering abilities, items, weather, Tailwind, Trick Room,
 * and optional stat-stage boosts via conditions.speBoost.
 */
export function getEffectiveSpeed(pokemon, conditions = {}) {
  const stats = getEffectiveStats(pokemon);
  if (!stats) return 0;
  let speed = stats.spe;

  // Stat-stage boost (e.g. after Dragon Dance)
  if (conditions.speBoost) speed = Math.floor(speed * stageMultiplier(conditions.speBoost));

  // Choice Scarf
  if (pokemon.item === 'Choice Scarf') speed = Math.floor(speed * 1.5);

  // Weather speed doublers
  const ability = pokemon.ability || '';
  if (conditions.weather === 'sun' && ability === 'Chlorophyll') speed *= 2;
  if (conditions.weather === 'rain' && ability === 'Swift Swim') speed *= 2;
  if (conditions.weather === 'sand' && ability === 'Sand Rush') speed *= 2;
  if (conditions.weather === 'snow' && ability === 'Slush Rush') speed *= 2;

  // Tailwind
  if (conditions.tailwind) speed *= 2;

  return Math.floor(speed);
}

/**
 * Estimate damage as a percentage of defender's HP.
 * Returns { minPct, maxPct, hko } or null if move data unavailable.
 */
export function estimateDamage(attacker, moveName, defender, conditions = {}) {
  const moveType = MOVE_TYPES[moveName];
  if (!moveType) return null; // status move

  const data = MOVE_DATA[moveName];
  if (!data || data.power === 0) return null; // unknown or variable power

  const atkStats = getEffectiveStats(attacker);
  const defStats = getEffectiveStats(defender);
  if (!atkStats || !defStats) return null;

  const isPhysical = data.cat === 'physical';
  let atkStat = isPhysical ? atkStats.atk : atkStats.spa;
  let defStat = isPhysical ? defStats.def : defStats.spd;
  const hpStat = defStats.hp;

  // Stat-stage boosts (Dragon Dance, Swords Dance, etc.)
  const attackerBoosts = conditions.attackerBoosts || {};
  const defenderBoosts = conditions.defenderBoosts || {};
  const atkBoost = isPhysical ? attackerBoosts.atk : attackerBoosts.spa;
  const defBoost = isPhysical ? defenderBoosts.def : defenderBoosts.spd;
  if (atkBoost) atkStat = Math.floor(atkStat * stageMultiplier(atkBoost));
  if (defBoost) defStat = Math.floor(defStat * stageMultiplier(defBoost));

  let power = data.power;

  // Fainted-ally power scaling — Last Respects: BP = 50 + 50 * fainted teammates,
  // capped at 250 (i.e. 5 fainted). Pass conditions.faintedAllies (count of
  // attacker's KO'd teammates, NOT including the active attacker itself).
  if (moveName === 'Last Respects') {
    const fainted = Math.max(0, Math.min(5, conditions.faintedAllies || 0));
    power = 50 + 50 * fainted;
  }

  // Ability-based attack modifiers
  const atkAbility = attacker.ability || '';
  const defAbility = defender.ability || '';

  if ((atkAbility === 'Huge Power' || atkAbility === 'Pure Power') && isPhysical) atkStat *= 2;
  if (atkAbility === 'Guts' && isPhysical) atkStat = Math.floor(atkStat * 1.5);
  if (atkAbility === 'Solar Power' && !isPhysical && conditions.weather === 'sun') atkStat = Math.floor(atkStat * 1.5);

  // Ability-based power modifiers
  if (atkAbility === 'Technician' && power <= 60) power = Math.floor(power * 1.5);
  if (atkAbility === 'Mega Launcher' && data.pulse) power = Math.floor(power * 1.5);
  if (atkAbility === 'Iron Fist' && data.punch) power = Math.floor(power * 1.2);
  if (atkAbility === 'Strong Jaw' && data.bite) power = Math.floor(power * 1.5);
  if (atkAbility === 'Sharpness' && data.slicing) power = Math.floor(power * 1.5);
  if (atkAbility === 'Sheer Force') power = Math.floor(power * 1.3);
  if (atkAbility === 'Tough Claws') power = Math.floor(power * 1.3);
  if (atkAbility === 'Reckless' && ['Flare Blitz','Brave Bird','Double-Edge','Head Smash','Wave Crash','Wild Charge','Volt Tackle','Take Down','Head Charge'].includes(moveName)) power = Math.floor(power * 1.2);
  if (atkAbility === 'Adaptability') { /* handled in STAB below */ }

  // Sand Force
  if (atkAbility === 'Sand Force' && conditions.weather === 'sand' && ['Ground','Rock','Steel'].includes(moveType)) {
    power = Math.floor(power * 1.3);
  }

  // Opponent Intimidate already active (approximation: reduce physical Atk)
  if (isPhysical && conditions.oppIntimidated) {
    atkStat = Math.floor(atkStat * 0.67);
  }

  // Defensive abilities
  if (defAbility === 'Multiscale' && conditions.defFullHp !== false) defStat = Math.floor(defStat * 1.5); // approximation
  if (defAbility === 'Fur Coat' && isPhysical) defStat *= 2;
  if (defAbility === 'Thick Fat' && (moveType === 'Fire' || moveType === 'Ice')) atkStat = Math.floor(atkStat * 0.5);
  if ((defAbility === 'Filter' || defAbility === 'Solid Rock') && getEffectiveness(moveType, defender.types) > 1) { /* handled in multiplier */ }

  // Calc base damage
  let damage = Math.floor(Math.floor(Math.floor(22 * power * atkStat / defStat) / 50) + 2);

  // Modifiers chain
  let modifier = 1.0;

  // Spread move — 0.75x only applies in doubles when the move actually hits
  // multiple targets. In singles a "spread" move (Earthquake, Surf, etc.)
  // hits at full power. Default to doubles when format is unspecified to
  // preserve historical behaviour.
  const format = conditions.format || 'doubles';
  if (data.spread && format === 'doubles') modifier *= 0.75;

  // Weather
  if (conditions.weather === 'sun' && moveType === 'Fire') modifier *= 1.5;
  if (conditions.weather === 'sun' && moveType === 'Water') modifier *= 0.5;
  if (conditions.weather === 'rain' && moveType === 'Water') modifier *= 1.5;
  if (conditions.weather === 'rain' && moveType === 'Fire') modifier *= 0.5;

  // STAB
  const isStab = attacker.types?.includes(moveType);
  if (isStab) modifier *= (atkAbility === 'Adaptability' ? 2.0 : 1.5);

  // Type effectiveness
  const effectiveness = getEffectiveness(moveType, defender.types);
  modifier *= effectiveness;

  // Filter / Solid Rock
  if ((defAbility === 'Filter' || defAbility === 'Solid Rock') && effectiveness > 1) modifier *= 0.75;

  // Immunity check
  if (effectiveness === 0) return { minPct: 0, maxPct: 0, hko: 'immune' };

  // Ability immunities
  if (defAbility === 'Water Absorb' && moveType === 'Water') return { minPct: 0, maxPct: 0, hko: 'immune' };
  if (defAbility === 'Volt Absorb' && moveType === 'Electric') return { minPct: 0, maxPct: 0, hko: 'immune' };
  if (defAbility === 'Flash Fire' && moveType === 'Fire') return { minPct: 0, maxPct: 0, hko: 'immune' };
  if (defAbility === 'Lightning Rod' && moveType === 'Electric') return { minPct: 0, maxPct: 0, hko: 'immune' };
  if (defAbility === 'Motor Drive' && moveType === 'Electric') return { minPct: 0, maxPct: 0, hko: 'immune' };
  if (defAbility === 'Sap Sipper' && moveType === 'Grass') return { minPct: 0, maxPct: 0, hko: 'immune' };
  if (defAbility === 'Earth Eater' && moveType === 'Ground') return { minPct: 0, maxPct: 0, hko: 'immune' };
  if (defAbility === 'Levitate' && moveType === 'Ground') return { minPct: 0, maxPct: 0, hko: 'immune' };
  if (defAbility === 'Dry Skin' && moveType === 'Water') return { minPct: 0, maxPct: 0, hko: 'immune' };

  // Random factor: 85%-100%
  const minDamage = Math.floor(damage * modifier * 0.85);
  const maxDamage = Math.floor(damage * modifier * 1.0);

  const minPct = Math.round((minDamage / hpStat) * 100);
  const maxPct = Math.round((maxDamage / hpStat) * 100);

  let hko;
  if (minPct >= 100) hko = 'OHKO';
  else if (minPct >= 50) hko = '2HKO';
  else if (minPct >= 34) hko = '3HKO';
  else hko = '4HKO+';

  return { minPct, maxPct, hko };
}

/**
 * Generate a damage matrix: each attacker vs each defender with their best moves.
 */
export function generateDamageMatrix(teamA, teamB, conditions = {}) {
  const results = [];

  for (const attacker of teamA) {
    if (!attacker.moves || attacker.moves.length === 0) continue;
    if (!attacker.baseStats && !attacker.megaStats) continue;

    for (const defender of teamB) {
      if (!defender.baseStats && !defender.megaStats) continue;

      let bestResult = null;
      let bestMove = null;

      for (const move of attacker.moves) {
        const result = estimateDamage(attacker, move, defender, conditions);
        if (!result) continue;
        if (!bestResult || result.maxPct > bestResult.maxPct) {
          bestResult = result;
          bestMove = move;
        }
      }

      if (bestResult && bestMove) {
        results.push({
          attacker: attacker.name,
          defender: defender.name,
          move: bestMove,
          ...bestResult,
        });
      }
    }
  }

  return results;
}
